//
// README - Beta Component
//
// This component is marked "beta" and will go through some iterations
// before landing at a final solution. Please reach out to the Cadence team
// if you intend to use this in the meantime and we'll help you out!
//

import React from 'react';
import classNames from 'classnames';

import {
  RCSkeleton,
  RCEyebrow, RCEyebrowProps,
  RCNudge, RCNudgeProps,
  RCPriceBlock, RCPriceBlockProps,
  RCCloseButton,
  RCShopInfo, RCShopInfoProps,
} from '../';
import { LinkElement } from '../helpers/linkElement';

export interface RCListingCardProps {
  /**
   * `linkElement` is Cadence's way to support CoreLink.
   * Pass in a linkable element and in component's internals will determine how to render the `children` prop passed.
   *
   * Example:
   *
   * ```tsx
   * <RCListingCard
   *  title="Foo"
   *  linkElement={(children) => (
   *    <CoreLink
   *      to="/where/ever/you/want/"
   *      target="_blank"
   *    >
   *      {children}
   *    </CoreLink>
   *  )}
   *  ...
   * />
   * ```
   */
  linkElement?: (children: React.ReactNode) => React.ReactElement | undefined;

  /**
   * We pass in an `<ImageCarousel>` here exclusively.
   */
  thumbnailElement: React.ReactElement;

  /** The title of the listing */
  title?: string;

  /** The condition of the listing. E.g. `Condition: Brand New`. This component is not responsible for appending `Condition: ` strings */
  conditionLabel?: string;

  /** Card-level param. If true, the card will take up the full height of its parent.
   * Should be `true` when inside a listing grid where all items in a row should be the same height. */
  fullHeight?: boolean;

  /** The small bit of UI under the thumbnail. Should showcase sale info, bump info, or things like that. */
  eyebrow?: RCEyebrowProps;

  /** A human-readable string that shows the listing price */
  price?: RCPriceBlockProps;

  /** The watch element that gets rendered on top of the thumbnail in the upper-right corner of the card */
  favoriteButtonElement?: React.ReactElement;

  /** A `primary` action should be a `primary` RCButton variant. Actions passed in here should be RCButtons, or CoreLinks using RCButton styling. */
  primaryAction?: React.ReactElement;

  /** a `secondary` action should be a `secondary` RCButton. */
  secondaryAction?: React.ReactElement;

  /** hide action buttons on mobile screen width */
  hideActionsOnMobile?: boolean;

  /** Structured interface - See RCNudge for details */
  nudges?: RCNudgeProps[];

  /** Pass in non-UI-rendering HTML content here to help with SEO. Will be injected as the first DOM item of the card */
  metaElement?: React.ReactElement;

  /** Render a remove button to the right of the title area */
  removeButton?: {
    onClick: () => void;
    label: string;
  },

  /** When present, renders a single line of text (that truncates via ellipsis) under the listing title */
  shopLocation?: string;

  /** When present, renders information about the shop. */
  shopInfo?: RCShopInfoProps;

  /** When present, renders a decorated RCEyebrow on the lower left corner of the image.
   * Note: thumbnailElement must be present for `violatorText` to also appear. */
  violatorText?: string;

  /** When present, prevents whitespace from collapsing to maintain horizontal content alignment across neighboring cards */
  maintainWhitespace?: boolean;

  /** Pass admin controls here; we pass in TileCurationView. This should be an unwrapped set of buttons. */
  adminControls?: React.ReactNode;
}

{/* eslint-disable react/jsx-props-no-spreading */}

export function RCListingCard({
  linkElement = undefined,
  thumbnailElement,
  eyebrow = undefined,
  title = undefined,
  conditionLabel = undefined,
  price = undefined,
  favoriteButtonElement = undefined,
  primaryAction = undefined,
  secondaryAction = undefined,
  nudges = undefined,
  fullHeight = true,
  metaElement = undefined,
  removeButton = undefined,
  shopLocation = undefined,
  shopInfo = undefined,
  violatorText = undefined,
  maintainWhitespace = false,
  hideActionsOnMobile = false,
  adminControls = undefined,
}: RCListingCardProps) {

  return (
    <div className={classNames(
      'rc-listing-card',
      {
        'rc-listing-card--full-height': fullHeight,
        'rc-listing-card--single-action': (!!primaryAction && !secondaryAction) || (!primaryAction && !!secondaryAction),
        'rc-listing-card--whitespace': maintainWhitespace,
        'rc-listing-card--hide-actions-on-mobile': hideActionsOnMobile,
      },
    )}>
      {metaElement && (
        <div className="rc-listing-card__meta">
          {metaElement}
        </div>
      )}
      <div className="rc-listing-card__inner">
        <div className="rc-listing-card__thumbnail">
          {thumbnailElement}
          {violatorText &&
            <div className="rc-listing-card__violator">
              <RCEyebrow type="default" text={violatorText} />
            </div>
          }
        </div>
        {favoriteButtonElement && (
          <div className="rc-listing-card__favorite-button">
            {favoriteButtonElement}
          </div>
        )}
        {(title || conditionLabel || eyebrow) && (
          <div className="rc-listing-card__header">
            {(title || conditionLabel) && (
              <div className="rc-listing-card__titleblock">
                {(maintainWhitespace || eyebrow) && (
                  <div className="rc-listing-card__eyebrow">
                    {eyebrow &&
                      <RCEyebrow {...eyebrow} />
                    }
                  </div>
                )}
                {title && (
                  <LinkElement styleName="rc-listing-card__title" linkElement={linkElement} >
                    {title}
                  </LinkElement>
                )}
                {conditionLabel && (
                  <div className="rc-listing-card__condition">
                    {conditionLabel}
                  </div>
                )}
              </div>
            )}
            {!!removeButton?.onClick && (
              <div className="rc-listing-card__title__remove-button">
                <RCCloseButton
                  aria-label={removeButton.label}
                  onClick={removeButton.onClick}
                  useAltIcon
                  showTooltip
                />
              </div>
            )}
          </div>
        )}
        {shopLocation && (
          <div className="rc-listing-card__shop-location">
            {shopLocation}
          </div>
        )}
        {price && (
          <div className="rc-listing-card__price">
            <RCPriceBlock
              {...price}
              fixedHeight={maintainWhitespace}
            />
          </div>
        )}
        {(primaryAction || secondaryAction) && (
          <div className="rc-listing-card__actions">
            {primaryAction && (
              <div className="rc-listing-card__action rc-listing-card__action--primary">
                {primaryAction}
              </div>
            )}
            {secondaryAction && (
              <div className="rc-listing-card__action rc-listing-card__action--secondary">
                {secondaryAction}
              </div>
            )}
          </div>
        )}
        {shopInfo && (
          <div className="rc-listing-card__shop-info">
            <RCShopInfo {...shopInfo} />
          </div>
        )}
        {!!nudges && (
          <div className="rc-listing-card__nudge-block">
            {nudges.map(nudge => (
              <div
                key={nudge.label}
                className="rc-listing-card__nudge"
              >
                <RCNudge {...nudge} />
              </div>
            ))}
          </div>
        )}
      </div>
      {!!adminControls && (
        <div className="rc-listing-card__admin-controls">
          {adminControls}
        </div>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="rc-listing-card">
      <div className="rc-listing-card__inner">
        <div className="rc-listing-card__thumbnail">
          <RCSkeleton
            shape="square"
            display="block"
          />
        </div>
        <div className="rc-listing-card__eyebrow"/>
        <div className="rc-listing-card__titleblock">
          <RCSkeleton count={2} />
          <div className="rc-listing-card__condition">
            <RCSkeleton width="13rem" />
          </div>
        </div>
        <div className="rc-listing-card__price">
          <RCSkeleton width="10rem" height="3.6rem" display="block" />
        </div>
      </div>
    </div>
  );
}

RCListingCard.Skeleton = Skeleton;
